import Konva from "konva";
import { getLayers, unScale } from "./grid";
import { MARKER_FUNCTION, type ICableModel } from "$lib/models/ICableModel";
import { get } from "svelte/store";
import { trenchStateStore } from "$lib/stores/trench-store";
import { object } from "yup";
import { Cartesian2 } from "cesium";
import { createTrenchMeasurements, createTrenchShape, getCoverThickness, getTrenchHeight, getTrenchWidth } from "$lib/core/map/trench-utils";
import { toast } from "$lib/components/organisms/toast/toast";

let activeObjects: (Konva.Circle | Konva.Rect | Konva.Text)[] = [];
// Keep track of the last intersected object. Once the intersection hits, we need to store it so that we don't  recalculate until intersection changes.
let lastIntersectedObject: Konva.Circle | Konva.Rect | null = null;

const getIntersectedObject = () => {
	return lastIntersectedObject;
}

const setIntersectedObject = (object: Konva.Circle | Konva.Rect | null) => {
  lastIntersectedObject = object;
}

const getActiveObjects = () => {
  return activeObjects;
}

/**
 * Lager et polygon for en sone i trapeset (horisontalt bånd).
 * startFromTop: avstand fra toppen (y=0) ned til start på sonen.
 * zoneHeight: høyden/tykkelsen på sonen.
 * Returnerer 4 punkter i rekkefølgen: BL, BR, TR, TL (samme som createTrenchShape).
 */
const createTrenchZone = (
	trenchData: ICableModel.TrenchData,
	startFromTop: number,
	zoneHeight: number
  ): Cartesian2[] => {
	const trenchHeight = getTrenchHeight(trenchData);
	const trenchWidth  = getTrenchWidth(trenchData);

	if (zoneHeight <= 0 || startFromTop >= trenchHeight) return [];
  
	// Vinkelberegning som i createTrenchShape
	const anglesBottom = (180 - (trenchData.trenchAngle * 2)) / 2;
	const alphaBetaRad = anglesBottom * (Math.PI / 180);
	const slopeTan = Math.tan(alphaBetaRad);
  
	// Klamre y-start og y-slutt til [0, trenchHeight]
	const yStart = Math.max(0, Math.min(startFromTop, trenchHeight));
	const yEnd   = Math.max(0, Math.min(yStart + zoneHeight, trenchHeight));
	if (yEnd <= yStart) return [];
  
	// Bredde i valgfri høyde y (y=0 i topp, y=trenchHeight i bunn)
	// Toppbredde = trenchWidth + 2*trenchHeight*slopeTan
	// Bredde(y)  = trenchWidth + 2*(trenchHeight - y)*slopeTan
	const widthAt = (y: number) => trenchWidth + 2 * (trenchHeight - y) * slopeTan;
  
	const wStart = widthAt(yStart);
	const wEnd   = widthAt(yEnd);
  
	// Returner polygon i samme rekkefølge som createTrenchShape:
	// BL(yEnd), BR(yEnd), TR(yStart), TL(yStart)
	return [
	  new Cartesian2(-wEnd / 2,   yEnd),
	  new Cartesian2( +wEnd / 2,  yEnd),
	  new Cartesian2( +wStart / 2, yStart),
	  new Cartesian2(-wStart / 2,  yStart),
	];
  };

const highlightPartZone = (partCoordinates: Cartesian2[]) => {
	const zone = new Konva.Line({
		id: 'highlightedZone',
		points: partCoordinates
		  .map((cartesianValue) => [
			meterToMillimeter(cartesianValue.x),
			meterToMillimeter(cartesianValue.y)
		  ])
		  .flat(1),
		stroke: 'rgba(83,59,217,1)',
		fill: 'rgba(83,59,217,0.5)',
		strokeWidth: 2,
		closed: true
	  });
	return zone;
}

const toggleHoverHint = (show: boolean, object?: Konva.Circle | Konva.Rect) => {
    if(!object || !show){
    // Remove the hints
      activeObjects.map((obj) => {
          obj.destroy();
      });
      activeObjects = [];
      return;
    }
    // All objects should contain a 'hint' attribute that references the same object.
    const hint = activeObjects[0]?.getAttr('hint');

    // If an object is provided without a hint or not at all, generate a warning. It should not happen.
    if(object && !object?.attrs?.hint){
      toast({
        type: 'warning',
        title: 'Ingen hint',
        description: 'Ingen hint tilknyttet markeringen.'
      })
      return;
    }
    // If the hint is already visible and active, do nothing.
    if(show && object?.attrs?.hint === hint) return; // If the hint is already shown for this object, do nothing

    if (show && object && object?.attrs?.hint) {
      // Show the hint
        
        const textHint = new Konva.Text({
            hint: hint,
            x: object.x(),
            y: object.y() - object.height() / 2 - unScale(30), // Position above the object
            text: object.attrs.descriptionText,
            fontSize: unScale(16),
            fontFamily: 'Poppins, Arial, sans-serif',
            fontStyle: '400',
            fill: 'white',
            padding: unScale(8),
        });
        const textBg = new Konva.Rect({
			x: textHint.x(),
			y: textHint.y(),
			cornerRadius: unScale(5),
			width: textHint.width(),
			height: textHint.height(),
			fill: 'rgba(12, 0, 62, 0.8)',
        });
        const { entityLayer } = getLayers();
        entityLayer.add(object, textBg, textHint);
        activeObjects = [object, textBg, textHint];
    }
};

const meterToMillimeter = (val: number) => {
  return val * 1000;
};

const createTrench2DShape = (trenchData: ICableModel.TrenchData) => {
  const trenchModel = new Konva.Line({
    points: createTrenchShape(trenchData)
      .map((cartesianValue) => [
        meterToMillimeter(cartesianValue.x),
        meterToMillimeter(cartesianValue.y)
      ])
      .flat(1),
    stroke: 'rgba(0, 0, 0, 0.8)',
    fill: 'rgba(135,135,135,0.5)',
    strokeWidth: 2,
    closed: true
  });

  return trenchModel;
};

const createTrench2DMeasurementRulers = (heightToMeasure: number, subtractFromTop = false, trenchData: ICableModel.TrenchData) => {
  const trenchRulerFoundation = new Konva.Line({
    points: createTrenchMeasurements({
      heightToMeasure,
      subtractFromTop,
      trenchData,
      mode: '2D'
    })
      .map((cartesianValue) => [
        meterToMillimeter(cartesianValue.x),
        meterToMillimeter(cartesianValue.y)
      ])
      .flat(1),
    stroke: 'rgba(73, 73, 255, 1)',
    strokeWidth: 2,
    dash: [unScale(6), unScale(6)]
  });
  return trenchRulerFoundation;
};

/**
 * To do: Adjust width of trench based on calculated result.
 *
 * Returns the total width in mm specified by cable width and cable offsets for selected cables
 */
const createCableArea = (cableZone: ICableModel.TrenchData["cableZones"][number]) => {
	const objectsInZones = cableZone.objects;

	if(!objectsInZones || objectsInZones.length === 0) return 0;

	const objects = objectsInZones.map(object => object.data);
  	const arrLength = objects.length;

  	const totalWidth = objects.reduce((sum, object, i) => {
		const isPipe = (o: ICableModel.TrenchObjectTypes): o is ICableModel.PipeData => {
			return 'pipeBaseData' in o && o.pipeBaseData.trenchObjectType === 'rør';
		}
		const isCable = (o: ICableModel.TrenchObjectTypes): o is ICableModel.Cable => {
			return 'cableConductorData' in o && o.cableConductorData.trenchObjectType === 'kabel';
		}
		const isEcc = (o: ICableModel.TrenchObjectTypes): o is ICableModel.EccCable => {
			return 'cableConductorData' in o && o.cableConductorData.trenchObjectType === 'jordtråd';
		}

		let diameter = 0;
		let gapSize = 0;
		let nextGap = 0;

		if(isPipe(object)){
			diameter = object.pipeBaseData.diameter;
			gapSize = 100; // Pipe default gap
		} else if (isCable(object)){
			diameter = object.cableConductorData.diameter;
			gapSize = 70; // Cable default gap
		} else if (isEcc(object)){
			diameter = object.cableConductorData.diameter;
			gapSize = 70; // ECC default gap
		}
		
		if(i !== arrLength - 1){
			const nextObject = objects[i + 1];
			if(isPipe(nextObject)){
				nextGap = 100; // Pipe default gap
			} else if (isCable(nextObject)){
				nextGap = 70; // Cable default gap
			} else if (isEcc(nextObject)){
				nextGap = 70; // ECC default gap
			}
		}

			
		const offset =
		i === arrLength - 1
			? 0
			// Pipe default offset is 0.1m, cables have a dynamic offset based on their minimal offset requirement.
			: Math.max(gapSize, nextGap); // The biggest offset requirement between current and next.
		return sum + diameter + offset;
  	}, 0);

  return totalWidth;
  
};

/**
 * TODO: DONT PREVIEW AS PART OF MVP. TOO COMPLEX. MAKE DRAGGING OVER ENTITY HIGHLIGHT THE TOUCHED ENTITY INSTEAD. MOUSEUP WILL REPLACE IF CONFIRMED.
 * @param cables 
 * @returns 
 */
const previewCablePosition = (cables: ICableModel.TrenchObject[]) => {
	if('cableConductorData' in cables === false){
		return; // Not a cable, do nothing
	}
	// This function should only consider array placement in the future. The only difference between this and the actual drawing of cables is that this function
	// contains a Circle shape replacing the selected Image and that it should be more cost-efficient as it's run on mouse move.
	// As soon as the mouse is released, the actual cable is drawn in place of the Circle shape and the cable area is recalculated. 

	// Get the cable that is currently being moved. We know the order (cables param), but not which is moved
	// We only want to mutate the existing cables. Sort by position only if order is changed.
	// This is the cable we need to move
	const previewCable = getActiveObjects().find(obj => obj instanceof Konva.Circle && obj.attrs?.hint);
	// Hint contains the externalCableId of the cable that is being moved.
	if(!previewCable || !previewCable.attrs?.hint) return;

	const cableDataStore = get(trenchStateStore)!.cableZones[0].objects.filter((object) => object.type === 'KABEL_HS' || object.type === 'KABEL_LS');
	// Which position is the original position of the dragged cable?
	const positionBeforeMove = cableDataStore.findIndex((cable) => cable.data.operationalMarking === previewCable.attrs.hint);
	// Which position is the current dragged cable in?
	const previewPosition = cables.findIndex((cable) => cable.data.operationalMarking === previewCable.attrs.hint);

	if(positionBeforeMove === previewPosition) return; // No change in position? no need to redraw

	const selectedCableData = cableDataStore[positionBeforeMove];

	// Okay, we need to move things around. Find an image. The image (cable). The image needs to swap positions with the previewCable (Circle shape).
	// Store the intersected object so that we avoid triggering more intersections on small movements right after the first one.
	const intersectedObject = lastIntersectedObject;
	if(intersectedObject){

		/***
		 * !!!!!!!!!!!!!!!!
		 * We should make a utility function that has a sole purpose of switching the position of two objects.
		 * !!!!!!!!!!!!!!!!
		 */

		const trenchData = get(trenchStateStore)!;
		const trenchHeight = getTrenchHeight(trenchData);
		const cableDepth = trenchHeight - trenchData.foundationZones.reduce((acc, zone) => acc + zone.depth, 0);
		
		const intersectedCable = cables.find(cable => cable.data.operationalMarking === intersectedObject.attrs.externalCableId && 'cableConductorData' in cable.data)?.data as ICableModel.Cable | undefined;
		const intersectedRadius = intersectedCable!.cableConductorData.diameter / 2;
		// To Image position (top left corner of image)
		// Now "reset/nullify" programmatically calculated offsets based on radius.
		const previewCoordinates =  previewCable.getPosition().x; //- intersectedRadius * 1000,

		// To Circle shape position (center of shape)
		const intersectCoordinates = intersectedObject.getPosition().x + intersectedRadius // Center bottom line should be the result

		// The object we collide with
		intersectedObject.position({
			x: previewCoordinates - intersectedRadius, 
			y: cableDepth * 1000 - intersectedRadius * 2
		}).draw();

		// The object we preview
		previewCable.position({
			x: intersectCoordinates , 
			y: cableDepth * 1000 - ((selectedCableData.data as ICableModel.Cable).cableConductorData.diameter / 2)
		}).draw();
	}

	return;

}
// Typeguard for Pipe
const isPipe = (o: ICableModel.TrenchObjectTypes): o is ICableModel.PipeData => {
	// Check if the object has a pipeBaseData property, which is unique to Pipe
	return 'pipeBaseData' in o && o.pipeBaseData.trenchObjectType === 'rør';
};
const isCable = (o: ICableModel.TrenchObjectTypes): o is ICableModel.Cable => {
	return 'cableConductorData' in o && o.cableConductorData.trenchObjectType === 'kabel';
}
const isEcc = (o: ICableModel.TrenchObjectTypes): o is ICableModel.EccCable => {
	return 'cableConductorData' in o && o.cableConductorData.trenchObjectType === 'jordtråd';
}

const getObjectDefaultMeasurementsByType = (previousObject: ICableModel.TrenchObjectTypes) => {
	if(isPipe(previousObject)){
		return {
			diameter: previousObject.pipeBaseData.diameter,
			gap: 100 // Pipe default gap
		}
	} else if (isCable(previousObject)){
		return { 
			diameter: previousObject.cableConductorData.diameter,
			gap: 70 // Cable default gap
		};
	} else if (isEcc(previousObject)){
		return { 
			diameter: previousObject.cableConductorData.diameter,
			gap: 70
		}; // ECC default gap
	} else {
		// Unknown object
		return {
			diameter: 0,
			gap: 0
		};
	}
}

const drawEccObject = ({x, y, width, height, object}: {x: number, y: number, width: number, height: number, object: ICableModel.EccCable}) => {
	const {entityLayer} = getLayers();
	const strokeWidth = 1;
	const ecc = new Konva.Circle({
		x,
		y,
		width: width - strokeWidth,
		height: height - strokeWidth,
		fill: 'rgba(184, 115, 51, 1)',
		stroke: 'rgba(0,0,0,1)',
		strokeWidth
	});
	entityLayer.add(ecc);
	return ecc;
}

const drawPipeObject = ({x, y, width, height, object}: {x: number, y: number, width: number, height: number, object: ICableModel.PipeData}) => {
	const {entityLayer} = getLayers();
	const strokeWidth = object.pipeBaseData.borderWidth;
	const pipe = new Konva.Circle({
		x,
		y,
		width: width - strokeWidth,
		height: height - strokeWidth,
		fill: 'rgba(255, 255, 255, 1)',
		stroke: 'rgba(255,0,0,1)',
		strokeWidth,
	});
	entityLayer.add(pipe);
	return pipe;
}

const drawCableObject = ({x, y, width, height, object}: {x: number, y: number, width: number, height: number, object: ICableModel.Cable}) => {
	//const IMG_SOURCE = '/gfx/cable/tfxp1kv.svg';
	const {entityLayer} = getLayers();
	const strokeWidth = 1;
	const cable = new Konva.Circle({
		x,
		y,
		width: width - strokeWidth,
		height: height - strokeWidth,
		fill: object.cableConductorData.voltageLevel === 'HS' ? 'rgba(255,0,0,1)' : 'rgba(0, 0, 255, 1)',
		stroke: 'rgba(0,0,0,1)',
		strokeWidth
	});
	entityLayer.add(cable);
}

const drawMarking = ({x, y, width, height, object}: {x: number, y: number, width: number, height: number, object: ICableModel.MarkingBaseData}) => {
	const {entityLayer} = getLayers();
	const marking = new Konva.Rect({
		x,
		y,
		width,
		height,
		fill: object.markerFunction === MARKER_FUNCTION.ADVARSEL ? 'yellow' : 'red',
	});
	entityLayer.add(marking);
}

/**
 * TODO: Add a dynamic vector when SVGs are available.
 * TODO: Move x offset somewhere else? Do we know that this is where xoffset is first set at all times?
 *
 * Creates 2D shapes of cables and pipes and adds vector graphic. Data comes from registered cables in trench and its corresponding cable base data.
 * Offsets are made by cableBounds to position cables in center of trench.
 */
const createCable2DShape = (trenchData: ICableModel.TrenchData) => {
	// Create necessary width for cable area by calculating cable size and minimum offset requirements.
	for(let i = 0; i < trenchData.cableZones.length; i++){
		const cableZone = trenchData.cableZones[i];
		const trenchObjects = cableZone.objects.filter((object) => object.type === 'KABEL_HS' || object.type === 'KABEL_LS' || object.type === 'ROR' || object.type === 'JORD');
		const cableBounds = createCableArea(cableZone);
		// Get the transform width of the cable bounds (-50%) to center the cables in the trench.
		const cableOffset = cableBounds/2;

		// Store the x coordinates of cables to position them correctly. And order them
		let xCoords: number[] = [];

		trenchObjects.forEach((trenchObject, j) => {
			const object = trenchObject.data;
			let radiusToMm = 0;
			let xPosition = 0;

			const previousObject = trenchObjects[j - 1]?.data;
			let currentGap = 0;
			
			if(isPipe(object)){
				radiusToMm = object.pipeBaseData.diameter / 2;
				currentGap = 100; // Pipe default gap
			} else if (isCable(object)){
				radiusToMm = object.cableConductorData.diameter / 2;
				currentGap = 70; // Cable default gap
			} else if (isEcc(object)){
				radiusToMm = object.cableConductorData.diameter / 2;
				currentGap = 70; // ECC default gap
			}

			// Create correct offsets on all objects following the first
			if(j !== 0 && previousObject){
				const {
					diameter,
					gap
				} = getObjectDefaultMeasurementsByType(previousObject);
				
				xPosition = // Returns the start position of the new object to render
					xCoords[j - 1] + // Previous position
					diameter + // Previous size
					Math.max(gap, currentGap); // The biggest offset requirement between previous and current.
			}
			xCoords.push(xPosition);

			const objectDiameter = radiusToMm * 2;
			let xOffset = xPosition - cableOffset;

			// THIS UPDATE IS PURELY USED TO OFFSET CABLES IN 3D.
			// Todo: Store is not updated at this point? But parameters are. We have a potential race condition here.
			trenchStateStore.update(store => {
				// Adjust assuming Three js places 3D objects with origin center (Three.js Pipe).
				const adjustedXOffset = xOffset + radiusToMm;
				const storeObject = store?.cableZones[i].objects[j];
				if(!storeObject) return store; // No object found, do nothing. Should suppress errors.

				store.cableZones[i].objects[j].data.placement.offsetX = adjustedXOffset / 1000;
				return store;
			})

			const tallestItemDiameter = Math.max(...trenchObjects.map(trenchObject => {
				const {
					diameter
				} = getObjectDefaultMeasurementsByType(trenchObject.data)
				return diameter;
			}));

			const trenchHeight = getTrenchHeight(trenchData);
			// TODO: We need to consider the cable zones in the future. Make them dynamic by addin a cable zone's height based on index.
			const cableDepth = trenchHeight - trenchData.foundationZones.reduce((acc, zone) => acc + zone.depth, 0);

			if(isPipe(object)){
				const objectXCenter = xOffset + radiusToMm;
				drawPipeObject({ 
					x: objectXCenter, 
					y: meterToMillimeter(cableDepth) - radiusToMm,
					width: objectDiameter,
					height: objectDiameter,
					object
				});
				// Render child objects. They do not require spacing or being registered as a separate xCoord at the moment as they are inside another object and wont affect the overall layout.
				if(trenchObject.childObjects?.length){
					for(let i = 0; i < trenchObject.childObjects.length; i++) {
						const childObject = trenchObject.childObjects[0];
						if(isPipe(childObject.data)){
                            toast({
                                type: 'warning',
                                title: 'Rør i rør',
                                description: 'Rør i rør er ikke støttet i 2D visning enda.'
                            })
							return;
						} else if(isCable(childObject.data)){
							const childRadius = childObject.data.cableConductorData.diameter / 2;
							drawCableObject({
								x: objectXCenter, // Center of pipe minus cable offset
								y: meterToMillimeter(cableDepth) - childRadius - object.pipeBaseData.borderWidth, // Bottom of pipe minus cable diameter minus wall thickness
								width: childRadius * 2,
								height: childRadius * 2,
								object: childObject.data
							})
						}
					};
				}
				if(object.markingData){
					const markerWidth = object.markingData.width;
					const markerHeight = object.markingData.height;
					drawMarking({
						x: objectXCenter - markerWidth/2,
						y: meterToMillimeter(cableDepth) - tallestItemDiameter - cableZone.margin * 1000 - markerHeight, // 5mm above the tallest item in the cable zone
						width: markerWidth,
						height: markerHeight,
						object: object.markingData
					})
				}
			} else if (isCable(object)){
				const objectXCenter = xOffset + radiusToMm;
				// Cables (Image) is drawn a with different "anchors" than primitive shapes
				drawCableObject({
					x: objectXCenter,
					y: meterToMillimeter(cableDepth) - radiusToMm,
					width: objectDiameter,
					height: objectDiameter,
					object
				});
				if(object.markingData){
					const markerWidth = object.markingData.width;
					const markerHeight = object.markingData.height;
					const centeredWidth = xOffset + radiusToMm;
					drawMarking({
						x: centeredWidth - markerWidth/2,
						y: meterToMillimeter(cableDepth) - tallestItemDiameter - cableZone.margin * 1000 - markerHeight, // 5mm above the tallest item in the cable zone
						width: markerWidth,
						height: markerHeight,
						object: object.markingData,
					});
				}
			} else if (isEcc(object)){
				const objectXCenter = xOffset + radiusToMm;
				drawEccObject({
					x: objectXCenter,
					y: meterToMillimeter(cableDepth) - radiusToMm,
					width: objectDiameter,
					height: objectDiameter,
					object
				});
				if(object.markingData){
					const markerWidth = object.markingData.width;
					const markerHeight = object.markingData.height;
					drawMarking({
						x: objectXCenter - markerWidth/2,
						y: meterToMillimeter(cableDepth) - tallestItemDiameter - cableZone.margin * 1000 - markerHeight, // 5mm above the tallest item in the cable zone
						width: markerWidth,
						height: markerHeight,
						object: object.markingData
					})
				}
			}
		});
	}
};

const createDrawings = (trenchData: ICableModel.TrenchData) => {
	// Trench model
	const { entityLayer, hintLayer, trenchLayer } = getLayers();
	let entitiesToDraw = [];
	trenchLayer.add(createTrench2DShape(trenchData));

	if (trenchData.cableZones.length && trenchData.cableZones.some(zone => zone.objects.length > 0)) {
		// Only draw objects if there are any in the trench.
		createCable2DShape(trenchData);
	}
	const coverThickness = getCoverThickness(trenchData);
	entitiesToDraw.push(createTrench2DMeasurementRulers(trenchData.foundationZones.reduce((acc, zone) => acc + zone.depth, 0), false, trenchData));
	entitiesToDraw.push(createTrench2DMeasurementRulers(coverThickness, true, trenchData));
	entitiesToDraw.forEach((entityToDraw) => {
		trenchLayer.add(entityToDraw);
	});
};

export {
	getIntersectedObject,
	previewCablePosition,
	setIntersectedObject,
	getActiveObjects,
	createTrench2DShape,
  	createTrench2DMeasurementRulers,
  	createCableArea,
  	createCable2DShape,
  	createDrawings,
	createTrenchZone,
	highlightPartZone,
  	toggleHoverHint,
	getObjectDefaultMeasurementsByType,
	isPipe,
	isCable,
	isEcc
}